import Button from "@hudson/ui/components/FormFields/Button";

import ContinueButton from "@components/ContinueButton";
import Error from "@hudson/ui/components/Error";
import ConcentricLoader from "@hudson/ui/components/Loader/ConcentricLoader";
import { FormProvider, useForm } from "@hudson/ui/components/ReactHookForm";
import { getProfiles } from "@lib/auth/jwt";
import useContent from "@lib/use_content";
import userProfileLinkGroup from "@services/user_profile_link_group";
import { useState } from "react";
import Field from "./Field";

export default function Form({
  className = "",
  fields = [],
  onHide = () => {},
  showCancel = false,
}: {
  className?: string;
  fields: string[];
  onHide?: (success?: boolean) => void;
  showCancel?: boolean;
}) {
  const {
    data: cData,
    loading,
  } = useContent([
    "beErrFieldRequired",
    "beErrLinkedGroups",
    "beProfileLgLabelClientid",
    "beProfileLgLabelData1",
    "beProfileLgLabelData2",
    "beProfileLgLabelGroupid",
    "beProfileLgLabelWebid",
    "beProfileLinkedGroupsCancelButton",
    "beProfileLinkedGroupsSubmitButton",
    "beProfileLinkedGroupsTitle",
    "beRequiredPlaceholder",
  ]);

  const [errorMessage, setErrorMessage] = useState<string>();
  const [submitting, setSubmitting] = useState(false);
  const formMethods = useForm();

  interface FormData {
    clientid?: string;
    data1?: string;
    data2?: string;
    groupid?: string;
    webid?: string;
  }

  interface UpdateData {
    clientId?: string;
    data1?: string;
    data2?: string;
    groupId?: string;
    webId?: string;
  }

  async function submitForm(data: FormData) {
    const profileId = getProfiles().user;
    if (!profileId) return;

    setErrorMessage("");
    setSubmitting(true);

    // Map the form field names to match the GraphQL mutation parameters
    const mappedData: UpdateData = {
      clientId: data.clientid,
      data1: data.data1,
      data2: data.data2,
      groupId: data.groupid,
      webId: data.webid,
    };

    const success = await userProfileLinkGroup(mappedData);

    if (!success) {
      setErrorMessage(cData?.content.beErrLinkedGroups);
    } else if (success) {
      onHide(true);
    }

    setSubmitting(false);
  }

  return loading ? (
    <ConcentricLoader className="max-w-2xl px-4 mx-auto opacity-60 sm:py-10 sm:px-6 lg:px-8" />
  ) : (
    <FormProvider {...formMethods}>
      <form
        id="link-group"
        onSubmit={(e) => {
          e.preventDefault();
          formMethods.handleSubmit(submitForm)();
        }}
        noValidate
      >
        <div className={className}>
          <div className="pb-8">
            <div className="grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-4 ">
              {fields.map((field) => (
                <Field
                  key={field}
                  errorMessage={cData?.content.beErrFieldRequired}
                  fieldName={field.toLowerCase()}
                  label={
                    cData?.content[
                      `beProfileLgLabel${field[0].toUpperCase()}${field.toLowerCase().slice(1)}`
                    ]
                  }
                  requiredPlaceholder={cData?.content.beRequiredPlaceholder}
                />
              ))}
            </div>
          </div>

          <div className="mt-2">
            {errorMessage && <Error className="mb-6" message={errorMessage} heavyStyle scrollTo />}

            <div className="flex items-center gap-x-8">
              {showCancel && (
                <div>
                  <Button type="button" asLink onClick={onHide}>
                    {cData?.content.beProfileLinkedGroupsCancelButton}
                  </Button>
                </div>
              )}

              <ContinueButton
                alignLeft
                className="justify-self-start"
                disabled={submitting}
                label={cData?.content.beProfileLinkedGroupsSubmitButton}
                loading={submitting}
              />
            </div>
          </div>
        </div>
      </form>
    </FormProvider>
  );
}
